/**
 * The teaching content behind "Learn the dials".
 *
 * Every control gets a paragraph for the selected-control card, then a deep
 * dive for the "Learn more" sheet: sections of plain-English explanation and,
 * for the controls that change how pictures look, a row of simulated sample
 * frames showing the same scene at different settings.
 *
 * Facts follow the Nikon Z fc Reference Guide (onlinemanual.nikonimglib.com/zfc):
 * dial ranges and lock behaviour from the camera-controls pages, the C
 * position from the exposure-compensation page, H1/H2 and Auto ISO from the
 * ISO dial page. Keep claims traceable to it.
 */

import { isPrime, type Lens } from './lens'
import type { ControlId } from './types'

/** Knobs for one simulated sample frame. All 0..1 except exposure (-1..1). */
export interface FrameParams {
  /** How much the moving subject streaks. */
  motion: number
  /** How much the background melts away. */
  dof: number
  /** Visible grain. */
  grain: number
  /** Overall brightness shift. */
  exposure: number
}

export interface FrameVariant {
  /** The setting this frame represents, e.g. "1/1000". */
  label: string
  caption: string
  params: FrameParams
}

export interface GuideSection {
  heading: string
  body: string
}

/**
 * A plain value-to-effect reference: what turning this control up or down
 * actually does. The question "is a higher number brighter or darker?" should
 * never need reading a paragraph to answer.
 */
export interface GuideScale {
  heading: string
  /** What one step along this control is worth, stated once. */
  note: string
  rows: Array<{
    /** The engraved value or range. */
    value: string
    /** What it does to the picture. */
    effect: string
    /** Brightness direction, for the arrow and tint. */
    tone: 'brighter' | 'neutral' | 'darker'
  }>
}

export interface DialGuide {
  /** Paragraph shown on the selected-control card. */
  blurb: string
  /** Quick reference for what higher and lower values do. */
  scale?: GuideScale
  /** "Learn more" sheet content. */
  sections: GuideSection[]
  /** Simulated looks at different settings, if this control changes the look. */
  examples?: {
    intro: string
    variants: FrameVariant[]
  }
}

const NEUTRAL: FrameParams = { motion: 0, dof: 0.25, grain: 0.06, exposure: 0 }

export const DIAL_GUIDE: Record<ControlId, DialGuide> = {
  modeLever: {
    blurb:
      'This little lever is the master switch of the whole camera: it decides which of the other dials actually do anything. The letters mean who is in charge — AUTO hands everything to the camera, P lets it pick while you nudge, S and A split the job between you, and M gives you all three controls. Nothing else on the top plate makes sense until you know where this lever points.',
    scale: {
      heading: 'Which number you turn, and what higher does',
      note: 'The big surprise: in P, S and A, turning your number does NOT make the picture brighter or darker — the camera silently moves the other setting to hold the exposure steady. You are changing the look, not the brightness. Only in M does a higher number actually darken the photo.',
      rows: [
        {
          value: 'AUTO',
          effect: 'No number is yours. The camera sets shutter, aperture and ISO, and ignores your dials.',
          tone: 'neutral',
        },
        {
          value: 'P',
          effect: 'Rear dial. Rolling it swaps one pair for another — faster shutter with a wider iris, or slower with a smaller one. Brightness never changes; only the look does.',
          tone: 'neutral',
        },
        {
          value: 'S',
          effect: 'Shutter dial. Higher number (500 → 4000) = shorter exposure = more frozen motion. The camera opens the iris to keep brightness the same, until it runs out of aperture.',
          tone: 'neutral',
        },
        {
          value: 'A',
          effect: 'Front dial. Higher f-number (f/4 → f/11) = smaller opening = more of the scene in focus. The camera slows the shutter to keep brightness the same, until it gets too slow to hand-hold.',
          tone: 'neutral',
        },
        {
          value: 'M',
          effect: 'Both dials, and nothing compensates. Higher shutter number darkens. Higher f-number darkens. Higher ISO brightens. The meter only tells you how far off you are.',
          tone: 'darker',
        },
      ],
    },
    sections: [
      {
        heading: 'What the five positions mean',
        body: 'AUTO: the camera chooses everything, including ISO — your dials are decoration. P (program): the camera picks a shutter/aperture pair; the rear dial slides along equivalent pairs. S (shutter-priority): you set the shutter speed, the camera finds an aperture. A (aperture-priority): you set the aperture, the camera finds a shutter speed — the mode most people live in. M (manual): you set both, and the meter just tells you how far off you are.',
      },
      {
        heading: 'The A vs AUTO trap',
        body: 'A and AUTO sit next to each other and are not remotely the same thing. In AUTO the camera ignores your ISO dial entirely (it shows ISO-A and does what it wants). In A you are making the creative choice — how blurry the background is — and keeping your ISO. If the app tells you "A" and you land on AUTO, every other instruction silently stops working.',
      },
      {
        heading: 'Which dials are live in which mode',
        body: 'Shutter dial: S and M only. Front dial (aperture): A and M only. ISO dial: everything except AUTO. Exposure comp dial: everywhere except M (where it only moves the meter — unless Auto ISO is switched on in the menu, in which case it changes real brightness by moving ISO). This is the single most confusing thing about the Z fc, and it is why this simulator greys dials out.',
      },
      {
        heading: 'Which mode should you actually use?',
        body: 'Start in A: choose how much background blur you want and let the camera do arithmetic. Switch to S when motion is the point — sport, kids, panning. Use M when you want identical frames shot after shot: panoramas, product shots on a table, studio-style setups, or any time the light is steady and you want the camera to stop changing its mind.',
      },
    ],
  },

  shutterDial: {
    blurb:
      'This dial sets how long the shutter stays open — each engraved number is a fraction of a second, and each click is a full stop: twice or half the light of its neighbour. It is also the dial most likely to be doing nothing: it only works in S and M. The red 1–4 are whole seconds, X is the flash sync speed, and B, T and 1/3 STEP are special positions with their own rules.',
    scale: {
      heading: 'What each number does',
      note: 'The engraved numbers are denominators: a bigger number means a shorter exposure. So turning towards 4000 freezes more motion and darkens the photo; turning towards the red seconds blurs more motion and brightens it. Each click is one stop — double or half the light.',
      rows: [
        { value: '4000 · 2000', effect: '1/4000–1/2000. Freezes anything. Needs bright sun or a wide aperture.', tone: 'darker' },
        { value: '1000 · 500', effect: 'Stops sport and running children cleanly.', tone: 'darker' },
        { value: '250', effect: 'Kids, dogs, street. The lowest speed that reliably stops people moving.', tone: 'darker' },
        { value: '125', effect: 'Posed people. They sway more than you think — this is the safe floor for portraits.', tone: 'neutral' },
        { value: '60 · 30', effect: 'Static subjects with steady hands. Below this, your own shake starts to show.', tone: 'brighter' },
        { value: '15 · 8 · 4 · 2', effect: '1/15 to 1/2. Tripod territory. Handheld only with support and a still subject.', tone: 'brighter' },
        { value: '1 · 2 · 4 (red)', effect: 'Whole seconds. Silky waterfalls, empty night streets. Needs something solid under the camera.', tone: 'brighter' },
      ],
    },
    sections: [
      {
        heading: 'Reading the engravings',
        body: '4000 means 1/4000 of a second — the fastest this camera goes — down through 2000, 1000, 500 and so on to 2 (1/2 second). Then the colour changes: the red 1, 2 and 4 are whole seconds, not fractions. The centre lock button guards the special positions: press it to enter X or T, and to leave any of B, T, X or 1/3 STEP. Between the normal speeds you can spin freely — and you can even rotate into B from the red 4 without pressing.',
      },
      {
        heading: 'The special positions',
        body: 'X is the flash sync speed, 1/200 — the fastest speed at which the whole frame sees the flash. T (Time) opens the shutter on one press and closes it on the next: long exposures without holding the button, perfect on a tripod. B (Bulb) holds the shutter open only while you keep the button pressed. Both B and T work in M only. 1/3 STEP hands control to the rear command dial, which then moves in third-stops from 1/4000 all the way to 30 seconds — finer and further than the engraved dial itself.',
      },
      {
        heading: 'What the speed does to the picture',
        body: 'Fast speeds freeze; slow speeds streak. Working guidelines: 1/500 stops sport, 1/250 stops kids and dogs, 1/125 is safe for posed people (they sway more than you think), 1/60 for things that hold still. Below that, your own hands become the problem: on this camera the rule of thumb is 1 over the focal length times 1.5 — about 1/25 at 16mm, 1/80 at 50mm. The lens’s VR buys you roughly four extra stops, but only for still subjects — VR steadies you, never them.',
      },
      {
        heading: 'Slow on purpose',
        body: 'Slow shutter speeds are a creative tool, not just a failure mode. Waterfalls turn silky between 1/4 and 1 second. Panning — following a moving subject at 1/30 while you swing with them — keeps the subject sharp and smears the background into speed lines. City light trails want T mode, something solid under the camera, and 10–30 seconds. In all of these, what does not move stays crisp; what moves paints.',
      },
    ],
    examples: {
      intro: 'The same street corner, three shutter speeds. Watch the cyclist; the lamppost and the hills never move.',
      variants: [
        {
          label: '1/1000',
          caption: 'Frozen mid-pedal — fast enough for any motion.',
          params: { ...NEUTRAL, motion: 0 },
        },
        {
          label: '1/30',
          caption: 'A hint of streak — readable, but soft edges on whatever moves.',
          params: { ...NEUTRAL, motion: 0.45 },
        },
        {
          label: '1 sec',
          caption: 'Motion paints — the cyclist becomes a streak, the street stays sharp.',
          params: { ...NEUTRAL, motion: 1 },
        },
      ],
    },
  },

  isoDial: {
    blurb:
      'ISO is how hard the camera amplifies what the sensor caught — turn it up and dim scenes become usable, at the price of grain. Hold the centre lock button down while you turn this dial; it will not move otherwise. 100 is the cleanest the camera gets, 51200 is the top of the native range, and H1 and H2 above it are emergency territory. In AUTO mode this dial is ignored completely.',
    scale: {
      heading: 'What each number does',
      note: 'Higher is always brighter — each step doubles the brightness and roughly doubles the grain. Unlike shutter and aperture, ISO costs you nothing but quality: it changes no motion, no focus, only noise. Use the lowest number the light allows.',
      rows: [
        { value: '100 · 200', effect: 'Cleanest the camera gets. Bright sun, or anything on a tripod.', tone: 'darker' },
        { value: '400 · 800', effect: 'Overcast, open shade, bright rooms. Still effectively flawless.', tone: 'neutral' },
        { value: '1600 · 3200', effect: 'Dusk and daytime interiors. Slight texture in the shadows if you look closely.', tone: 'brighter' },
        { value: '6400', effect: 'Evening indoors. The honest working ceiling on this camera — visible grain, never ruinous.', tone: 'brighter' },
        { value: '12800 · 25600', effect: 'Trades real quality for the shot. Grain everywhere, shadows going mushy.', tone: 'brighter' },
        { value: '51200 · H1 · H2', effect: 'Emergency only. Heavy grain and washed-out colour — a photo you would otherwise not get at all.', tone: 'brighter' },
      ],
    },
    sections: [
      {
        heading: 'What ISO actually is',
        body: 'It is not film speed and the sensor does not get more sensitive. The sensor catches whatever light the aperture and shutter let in; ISO is how much the camera turns up the volume on that signal afterwards. Turn up a quiet signal and you also turn up its hiss — that hiss is the grain, and it shows in the shadows first. This is why a well-lit ISO 6400 shot can look cleaner than a dim ISO 1600 one.',
      },
      {
        heading: 'The dial itself',
        body: 'Native range 100–51200 in full stops, plus H1 and H2 — roughly one and two stops beyond, about 102400 and 204800 equivalent. H settings cost real quality (heavy grain, washed colour) and exist for get-the-shot-or-nothing moments. The dial only obeys you outside AUTO; in AUTO it reads ISO-A and the camera decides. You can also enable Auto ISO from the photo shooting menu in P, S, A and M — the camera then treats your dial position as the starting point and raises ISO above it when the light demands.',
      },
      {
        heading: 'A practical ladder for this camera',
        body: 'On the 16-50mm kit zoom: 100–400 is daylight and bright shade, indistinguishable from perfect. 800–1600 covers dusk and bright interiors, still very clean. 3200–6400 is the honest working range for evening indoors — visible texture if you look, never ruinous. 12800 and up trades real quality for the shot: grain everywhere, shadows go mushy. One habit worth building: it is almost always better to give the sensor more light (slower shutter, wider aperture) than to brighten a dark file afterwards.',
      },
    ],
    examples: {
      intro: 'The same evening scene as ISO climbs. Look at the sky and the shadows under the hills.',
      variants: [
        {
          label: 'ISO 100',
          caption: 'Clean everywhere — smooth sky, deep honest shadows.',
          params: { ...NEUTRAL, grain: 0.03 },
        },
        {
          label: 'ISO 6400',
          caption: 'Fine speckle in the shadows — visible if you look, fine in prints.',
          params: { ...NEUTRAL, grain: 0.4 },
        },
        {
          label: 'ISO 25600',
          caption: 'Grain everywhere, colour thinning out — worth it only over a missed shot.',
          params: { ...NEUTRAL, grain: 0.95 },
        },
      ],
    },
  },

  expCompDial: {
    blurb:
      'This dial overrides the camera’s idea of "correctly exposed" — plus makes the photo brighter, minus darker, up to three stops each way in third-stop clicks. It exists because the meter can be fooled. It is also the most dangerous dial on the camera: it keeps its position between outings, and a forgotten −1 quietly darkens every photo you take next week.',
    scale: {
      heading: 'What each number does',
      note: 'Every whole number is one stop — a doubling or halving of the light. The dots between them are thirds, for fine-tuning. Plus is always brighter, minus is always darker.',
      rows: [
        { value: '+3', effect: 'Eight times brighter than the meter chose. Rare: snow in full sun, or a face against a white sky.', tone: 'brighter' },
        { value: '+2', effect: 'Four times brighter. Backlit portraits, bright beaches, white walls, heavy fog.', tone: 'brighter' },
        { value: '+1', effect: 'Twice as bright. The everyday nudge when faces come out muddy.', tone: 'brighter' },
        { value: '+⅓ · +⅔', effect: 'A gentle lift. Most scenes that need help only need this much.', tone: 'brighter' },
        { value: '0', effect: 'Whatever the meter decided. Right most of the time — leave it here by default.', tone: 'neutral' },
        { value: '−⅓ · −⅔', effect: 'A slight hold-back. Protects highlights that are just starting to blow out.', tone: 'darker' },
        { value: '−1', effect: 'Half as bright. Deepens mood and saves bright skies from washing out.', tone: 'darker' },
        { value: '−2', effect: 'A quarter as bright. A spotlit subject on a dark stage; a black dog the meter turned grey.', tone: 'darker' },
        { value: '−3', effect: 'An eighth. Silhouettes and near-black frames, on purpose.', tone: 'darker' },
      ],
    },
    sections: [
      {
        heading: 'Why the meter needs overriding',
        body: 'The meter assumes every scene averages out to a middle grey. Point it at snow, a white wall or a bright window and it dutifully darkens the shot to make all that white grey — so you dial plus to put the brightness back. Point it at a black cat on a dark sofa and it brightens the scene to grey — so you dial minus. The rule feels backwards at first: bright scenes need plus, dark scenes need minus.',
      },
      {
        heading: 'The C position',
        body: 'At C the dial steps aside and compensation moves to a command dial via Nikon’s "Easy exposure compensation" custom setting — the rear dial in AUTO and A, the front dial in P and S. Convenient once you know it, mystifying if you land on C by accident and a command dial starts changing your brightness. If your photos are inexplicably dark or bright, check this dial first, then check whether it sits on C.',
      },
      {
        heading: 'What it does in M',
        body: 'In manual mode you have already chosen the shutter, aperture and ISO, so there is nothing left for compensation to change — the dial only shifts the meter’s zero point. One real exception: if Auto ISO is enabled from the menu while in M, the camera changes ISO to honour the compensation, and brightness genuinely moves. With flash, compensation adjusts flash output and ambient exposure together.',
      },
    ],
    examples: {
      intro: 'The meter’s version, then the dial pushed two stops each way.',
      variants: [
        {
          label: '−2',
          caption: 'Moody and dark — highlights survive, shadows go black.',
          params: { ...NEUTRAL, exposure: -0.75 },
        },
        {
          label: '±0',
          caption: 'What the meter wanted — balanced, middle-of-the-road.',
          params: NEUTRAL,
        },
        {
          label: '+2',
          caption: 'Airy and bright — shadows open up, the sky starts to wash out.',
          params: { ...NEUTRAL, exposure: 0.75 },
        },
      ],
    },
  },

  mainCommandDial: {
    blurb:
      'The rear dial is the shape-shifter: its job changes with the mode lever. In P it slides along equivalent shutter/aperture pairs. With the shutter dial parked at 1/3 STEP in S or M, it takes over shutter speed entirely — in finer steps and a wider range than the engraved dial. And with the comp dial on C in AUTO or A, it carries exposure compensation.',
    sections: [
      {
        heading: 'Flexible program, demystified',
        body: 'In P the camera has picked, say, 1/250 at f/8. Roll the rear dial and it walks along the other pairs that admit exactly the same light: 1/500 at f/5.6, 1/1000 at f/4, or the other way to 1/125 at f/11. Brightness never changes; the character does — faster-and-blurrier versus slower-and-deeper. It is a genuinely quick way to shoot: let the camera meter, then roll the dial until the pair suits the subject.',
      },
      {
        heading: 'The 1/3 STEP takeover',
        body: 'The engraved dial moves in full stops and stops at 4 seconds. Set it to 1/3 STEP and the rear dial takes over with third-stop precision across a bigger range — 1/4000 down to 30 full seconds. Serious shooters often just live here: the engraved dial becomes a beautiful switch that says "the rear dial is in charge now".',
      },
    ],
  },

  subCommandDial: {
    blurb:
      'The front dial — the wheel by the shutter button — is where the aperture lives, because Z-mount lenses have no aperture ring on the barrel. In A and M, rolling it opens and closes the iris inside the lens; the little window on the top plate shows the current f-number. Smaller f-number, bigger opening: more light, blurrier background.',
    scale: {
      heading: 'What each number does',
      note: 'This is the one that reads backwards: a HIGHER f-number is a SMALLER opening, so it darkens the photo and puts more of the scene in focus. Lower numbers mean more light and a blurrier background. On the 16-50mm the lowest you can reach depends on the zoom — f/3.5 at 16mm, only f/6.3 at 50mm.',
      rows: [
        { value: 'f/3.5 · f/4.5', effect: 'As wide as this zoom opens. Most light, softest background — available only at the wide end.', tone: 'brighter' },
        { value: 'f/5.6', effect: 'Wide open at 35mm. Sharp, still separates a subject from its background.', tone: 'brighter' },
        { value: 'f/6.3 · f/7.1', effect: 'Wide open at 50mm. The most blur this lens gives at the long end.', tone: 'neutral' },
        { value: 'f/8', effect: 'The sweet spot. Sharpest corner-to-corner, deep enough for most scenes. The safe default.', tone: 'neutral' },
        { value: 'f/11', effect: 'Deep focus front to back — landscapes, architecture. Diffraction just beginning.', tone: 'darker' },
        { value: 'f/16 · f/22+', effect: 'Everything in focus, but the whole frame softens and you lose a lot of light. Rarely worth it.', tone: 'darker' },
      ],
    },
    sections: [
      {
        heading: 'Why the numbers feel upside-down',
        body: 'An f-number is a fraction — f/4 means the opening is the focal length divided by 4. That is why smaller numbers mean bigger openings: f/3.5 is a wide iris drinking light, f/16 is a pinhole. Each full stop (2.8, 4, 5.6, 8, 11, 16…) halves the light of the one before. Two things ride along with the light: depth of field and sharpness character.',
      },
      {
        heading: 'Depth of field — what actually controls the blur',
        body: 'Three things decide how much of the scene is in focus: the f-number, how close you are to the subject, and the focal length. On this kit zoom (f/3.5 at 16mm, only f/6.3 by 50mm) the aperture alone cannot melt a background the way a fast prime can — so distance does the heavy lifting. Get close to the subject, zoom towards 50mm, and put real space between them and the background. That combination out-blurs any f-number this lens owns.',
      },
      {
        heading: 'The sweet spot and the pinhole trap',
        body: 'Lenses are softest at their extremes. Wide open the corners smear slightly; stopped down past f/11 the whole frame goes gently soft from diffraction — light bending around the tiny opening. On this sensor, f/5.6 to f/8 is the crisp middle: sharp centre, sharp corners, deep enough focus for most scenes. If you cannot decide, f/8 is the classic answer.',
      },
    ],
    examples: {
      intro: 'The same subject, three apertures. Watch the hills and the sun behind them.',
      variants: [
        {
          label: 'f/3.5',
          caption: 'Background softens — as much separation as this lens’s aperture alone gives; getting close does the rest.',
          params: { ...NEUTRAL, dof: 1 },
        },
        {
          label: 'f/8',
          caption: 'The sweet spot — subject crisp, background readable but calm.',
          params: { ...NEUTRAL, dof: 0.35 },
        },
        {
          label: 'f/16',
          caption: 'Everything in focus front to back — at the cost of a touch of overall softness.',
          params: { ...NEUTRAL, dof: 0 },
        },
      ],
    },
  },

  photoVideoLever: {
    blurb:
      'The small lever under the shutter dial switches the camera between stills and video — and they keep separate settings, so your photo setup survives a detour into filming. Exposure works the same way in video, with one big difference: the shutter speed stops being a creative free choice and becomes tied to the frame rate.',
    sections: [
      {
        heading: 'Video changes the shutter rules',
        body: 'A video frame cannot expose longer than the frame takes to show, so at 30 frames per second your shutter cannot go slower than 1/30. The classic guideline runs the other way too: for natural-looking motion blur, shoot near double your frame rate — about 1/60 for 30p, 1/50 for 25p. Much faster and footage turns stuttery and clinical; that staccato look is a choice, not a default.',
      },
      {
        heading: 'Bright light is the hard part',
        body: 'Locking the shutter near 1/60 removes your main brightness lever, and the kit zoom’s aperture only closes so far — so bright daylight video often has nowhere to dump light. That is what ND (neutral density) filters are for: sunglasses for the lens. If video in sunshine keeps overexposing, an ND filter on the front of the lens is the honest fix.',
      },
    ],
  },

  focalRing: {
    // Kit-zoom content; guideFor() swaps this for a prime-specific guide.
    blurb:
      'The wide ring on the lens changes how much of the world fits in the frame — 16mm takes in a whole street, 50mm picks out one face. On this camera the zoom also quietly moves two other things: the widest available aperture (f/3.5 at 16mm sliding to f/6.3 at 50mm) and how steady your hands need to be.',
    sections: [
      {
        heading: 'Zooming is not just cropping',
        body: 'Stepping back and zooming in produces a different photograph from stepping forward at a wide setting — perspective changes. Long focal lengths compress: backgrounds loom closer and flatter, flattering for faces. Wide settings exaggerate depth: near things huge, far things tiny, dramatic for streets and rooms. (In full-frame terms this lens spans 24–75mm — the classic walk-around range.)',
      },
      {
        heading: 'What the zoom silently costs',
        body: 'Two ceilings move as you zoom in. The widest aperture shrinks — f/3.5 at 16mm, about f/4.5 at 24mm, f/5.6 at 35mm, f/6.3 at 50mm — so the long end gathers only about a third of the light of the wide end — nearly two stops less. And the handshake threshold rises with magnification: about 1/25 is safe at 16mm, but 50mm wants 1/80. Zooming in at dusk therefore costs you twice, which is why the recommender sometimes backs off to 35mm on purpose.',
      },
    ],
  },
}

/**
 * Content that changes when the 24mm f/1.7 prime is mounted: no zoom ring to
 * teach, no VR, and an aperture that genuinely melts backgrounds.
 */
const PRIME_OVERRIDES: Partial<Record<ControlId, DialGuide>> = {
  focalRing: {
    blurb:
      'This lens has no zoom ring — 24mm is the one focal length it speaks, a 36mm view in full-frame terms: the classic walk-around framing, wide enough for a street, tight enough for a person. You zoom with your feet, and that is not a consolation prize — moving the camera changes the picture in ways a zoom ring never can.',
    sections: [
      {
        heading: 'Zooming with your feet',
        body: 'A zoom changes how much fits in the frame; your feet change the geometry of the picture itself. Step closer and the subject grows faster than the background — they separate, the background falls away. Step back and the scene compresses together. One focal length also trains your eye: after a week you start seeing 36mm-equivalent frames before you raise the camera, which is why so many photographers deliberately carry only a prime.',
      },
      {
        heading: 'What this prime trades away — and buys',
        body: 'No zoom, and no VR: this lens has no stabiliser and the Z fc body adds none, so the bare handshake rule applies — keep the shutter at about 1/40 or faster handheld. In exchange you get f/1.7: nearly four stops more light than the kit zoom offers at this focal length, which turns evening rooms from ISO 12800 territory into ISO 800, and real background blur the kit zoom cannot reach at any setting. It also focuses down to 18cm — close enough for plates and details.',
      },
    ],
  },
  subCommandDial: {
    blurb:
      'The front dial — the wheel by the shutter button — is where the aperture lives, because Z-mount lenses have no aperture ring on the barrel. On this prime the dial covers f/1.7 to f/11, and that top end changes everything: f/1.7 is a genuinely wide iris, with the shallow focus and low-light reach the kit zoom cannot offer.',
    scale: {
      heading: 'What each number does',
      note: 'This is the one that reads backwards: a HIGHER f-number is a SMALLER opening, so it darkens the photo and puts more of the scene in focus. Lower numbers mean more light and a blurrier background. This lens runs f/1.7 to f/11 — and f/1.7 gathers nearly four times the light of the kit zoom at this focal length.',
      rows: [
        { value: 'f/1.7', effect: 'Wide open. Maximum light and true shallow focus — but the sharp zone is millimetres deep up close. One eye, one subject.', tone: 'brighter' },
        { value: 'f/2 · f/2.8', effect: 'Still very shallow, a touch more margin for error. f/2.8 is the safe choice for two people side by side.', tone: 'brighter' },
        { value: 'f/4', effect: 'Clear subject separation with a background you can still read. Sharpens up noticeably from wide open.', tone: 'neutral' },
        { value: 'f/5.6 · f/8', effect: 'The crisp-everywhere zone. Groups, streets, scenes. f/8 is the safe default.', tone: 'darker' },
        { value: 'f/11', effect: 'Deepest focus this lens offers, and where it stops. Diffraction is just starting to soften the frame.', tone: 'darker' },
      ],
    },
    sections: [
      {
        heading: 'Why the numbers feel upside-down',
        body: 'An f-number is a fraction — f/4 means the opening is the focal length divided by 4. That is why smaller numbers mean bigger openings: f/1.7 is a wide iris drinking light, f/11 is a pinhole. Each full stop (1.7 → 2.8 → 4 → 5.6 → 8 → 11, roughly) halves the light of the one before. Two things ride along with the light: depth of field and sharpness character.',
      },
      {
        heading: 'Depth of field at f/1.7 — a scalpel, not a paintbrush',
        body: 'Wide open and close up, the zone of sharp focus is millimetres deep. That is the portrait look — one eye crisp, the world dissolved — but it punishes sloppy focusing: put the focus point on the near eye, not the face. Two people cannot both be sharp at f/1.7 unless they are the same distance away; stop down to f/2.8 or f/4 for pairs. For groups, f/5.6 and up.',
      },
      {
        heading: 'The sweet spot and the pinhole trap',
        body: 'Wide open this lens is a little dreamy in the corners — often exactly the look you wanted it for. Stopped to f/2.8–f/4 it sharpens right up while keeping real separation. f/5.6 to f/8 is the crisp-everywhere zone, and past f/11 diffraction gently softens the whole frame — which is where this lens stops anyway. A fair summary: f/1.7 for one subject, f/4 for two, f/8 for everything.',
      },
    ],
    examples: {
      intro: 'The same subject, three apertures on the prime. Watch the hills and the sun behind them.',
      variants: [
        {
          label: 'f/1.7',
          caption: 'Background dissolves — true shallow focus, one plane sharp.',
          params: { motion: 0, dof: 1, grain: 0.06, exposure: 0 },
        },
        {
          label: 'f/4',
          caption: 'Subject clearly separated, background soft but readable.',
          params: { motion: 0, dof: 0.55, grain: 0.06, exposure: 0 },
        },
        {
          label: 'f/8',
          caption: 'Sharp front to back — the crisp-everywhere zone.',
          params: { motion: 0, dof: 0.1, grain: 0.06, exposure: 0 },
        },
      ],
    },
  },
}

/** The guide for a control, adjusted for whichever lens is mounted. */
export function guideFor(control: ControlId, lens: Lens): DialGuide {
  if (isPrime(lens)) {
    return PRIME_OVERRIDES[control] ?? DIAL_GUIDE[control]
  }
  return DIAL_GUIDE[control]
}
